// Copyright 2025, University of Maryland, All Rights Reserved
import { useCallback, useEffect, useMemo, useRef, useState, type FC } from 'react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from '@/components/ui'
import { Loader2 } from 'lucide-react'

import { useServices } from '@/app-shell/services'
import type { NodeAdminViewProps } from '@/core/types'

export const ContractReviewAdminView: FC<NodeAdminViewProps> = ({
  workspaceId,
  orgId,
  config,
  onChange,
  registerSave,
}) => {
  const services = useServices()
  const apiClient = services.getClient()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const onChangeRef = useRef(onChange)
  useEffect(() => { onChangeRef.current = onChange }, [onChange])

  const title = (config?.title as string) ?? ''

  const missingContext = !orgId || !workspaceId
  const basePath = useMemo(() => {
    if (missingContext) return null
    return `/admin/org/${orgId}/ws/${workspaceId}/contract-review`
  }, [missingContext, orgId, workspaceId])

  const save = useCallback(async () => {
    if (!basePath) return false
    try {
      await apiClient.post(`${basePath}/config`, {
        title: title || null,
      })
      return true
    } catch (err) {
      console.error('[ContractReviewAdmin] Save failed:', err)
      return false
    }
  }, [apiClient, basePath, title])

  useEffect(() => {
    registerSave?.(save)
  }, [registerSave, save])

  useEffect(() => {
    if (!basePath) return

    const load = async () => {
      setLoading(true)
      setError(null)

      try {
        const { data } = await apiClient.get<{ title?: string }>(`${basePath}/config`)
        if (data.title) onChangeRef.current?.('title', data.title)
      } catch (err: unknown) {
        const axiosErr = err as { response?: { data?: { detail?: string } }; message?: string }
        setError(axiosErr?.response?.data?.detail ?? axiosErr?.message ?? 'Failed to load settings')
      }

      setLoading(false)
    }

    void load()
  }, [apiClient, basePath])

  if (missingContext) {
    return (
      <div className="space-y-4">
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="text-destructive">Workspace context unavailable</CardTitle>
            <CardDescription>
              Unable to load contract review settings because no organization or workspace is selected.
              Please select a workspace and try again.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 text-muted-foreground">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading settings…
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <Card className="border-border">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Application configuration</CardTitle>
          <CardDescription className="text-sm text-muted-foreground">
            Configure the contract review title. Pipelines run automatically in parallel.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <div className="text-sm text-red-600">{error}</div>
          )}

          <div className="space-y-2">
            <Label htmlFor="app-title">Title</Label>
            <Input
              id="app-title"
              placeholder="Contract Review"
              value={title}
              onChange={e => onChange?.('title', e.target.value || null)}
            />
            <p className="text-xs text-muted-foreground">
              Display name shown to users at the top of the application.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
